import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import searchIcon from '../assets/search.png';
import notificationIcon from '../assets/notification.png';
import api from '../sever/Api';
import { useSearch } from '../context/SearchContext';

const Navbar = ({ toggleSidebar }) => {
  // Use global search context so pages like Personality Test can react to navbar input
  const { searchQuery, setSearchQuery } = useSearch();
  const [isNotifOpen, setIsNotifOpen] = useState(false);
  const [showConfirmLogout, setShowConfirmLogout] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  const notifRef = useRef(null);
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  const fetchNotifications = async () => {
    setIsLoading(true);
    try {
      const { data } = await api.get('/api/test-results');
      const items = (data?.testResults || []).map((r) => ({
        id: r._id || r.id,
        title: `${(r.testType || '').toUpperCase()} test completed`,
        subtitle: typeof r.overallPercentage === 'number' ? `Score: ${r.overallPercentage}%` : undefined,
        date: r.completedAt || r.createdAt,
      }));
      // Only keep the latest 5
      setNotifications(items.slice(0, 5));
    } catch (err) {
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  };

  const toggleNotifications = async () => {
    const next = !isNotifOpen;
    setIsNotifOpen(next);
    if (next) {
      await fetchNotifications();
    }
  };

  useEffect(() => {
    const onClickOutside = (e) => {
      if (!isNotifOpen) return;
      const target = e.target;
      if (notifRef.current && !notifRef.current.contains(target)) {
        setIsNotifOpen(false);
      }
    };
    document.addEventListener('click', onClickOutside);
    return () => document.removeEventListener('click', onClickOutside);
  }, [isNotifOpen]);

  const handleAvatarClick = () => {
    setShowConfirmLogout(true);
  };

  const handleLogout = () => {
    try {
      // Use AuthContext logout function
      logout();
      setShowConfirmLogout(false);
    } catch (error) {
      console.error('Error during logout:', error);
      // Even if there's an error, try to navigate away
      navigate('/');
    }
  };

  return (
    <div className="bg-[#fcfaff] p-4 sm:p-5 flex flex-col sm:flex-row justify-between gap-4 shadow-sm">
      {/* Hamburger Menu Button */}
      <div className="lg:hidden flex items-center">
        <button
          onClick={toggleSidebar}
          className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-[#9C27B0] hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#9C27B0] transition-colors duration-200"
          aria-expanded="false"
        >
          <span className="sr-only">Open sidebar menu</span>
          <svg
            className="h-6 w-6"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Search Input */}
      <div className="search flex items-center bg-[#fff]  sm:w-[50%] p-3 rounded-lg gap-3">
        <img src={searchIcon} alt="Search" className="w-4 sm:w-5" />
        <input
          type="text"
          placeholder="Search tests, insights, or people..."
          className="w-full bg-transparent outline-none border-none text-sm sm:text-base"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      {/* Notification + Profile */}
      <div className="flex gap-2 sm:gap-3 justify-end">
        <div ref={notifRef} className="relative">
          <button type="button" onClick={(e) => { e.stopPropagation(); toggleNotifications(); }} className="nav-list relative pointer-events-auto" style={{ background: '#E0F2F1' }} aria-label="Notifications">
            <img src={notificationIcon} alt="notification" className="w-4 sm:w-5" />
            {notifications.length > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-[10px] leading-none px-1.5 py-0.5 rounded-full">
                {notifications.length}
              </span>
            )}
          </button>

          {isNotifOpen && (
            <div className="fixed right-4 top-16 w-80 max-h-96 overflow-auto bg-white shadow-lg rounded-lg border border-gray-100 z-[1000] pointer-events-auto">
              <div className="px-4 py-3 border-b">
                <p className="font-semibold text-sm">Notifications</p>
                <p className="text-xs text-gray-500">Saved and completed tests</p>
              </div>
              <div className="divide-y">
                {isLoading && (
                  <div className="p-4 text-sm text-gray-500">Loading...</div>
                )}
                {!isLoading && notifications.length === 0 && (
                  <div className="p-4 text-sm text-gray-500">No notifications</div>
                )}
                {!isLoading && notifications.map((n) => (
                  <div key={n.id} className="p-3 hover:bg-gray-50">
                    <p className="text-sm font-medium text-gray-800">{n.title}</p>
                    {n.subtitle && <p className="text-xs text-gray-500 mt-0.5">{n.subtitle}</p>}
                    {n.date && (
                      <p className="text-[10px] text-gray-400 mt-1">{new Date(n.date).toLocaleString()}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="nav-list" style={{ background: '#FF5722', borderRadius: '50%' }} onClick={handleAvatarClick}>
          <h6 style={{ color: '#fff' }} className="text-sm">
            {user ? (user.name ? user.name.split(' ').map(p => p[0]).join('').slice(0,2).toUpperCase() : `${user.firstName?.charAt(0)?.toUpperCase() || ''}${user.lastName?.charAt(0)?.toUpperCase() || ''}`) : 'U'}
          </h6>
        </div>
      </div>
      {/* Logout Confirm Modal */}
      {showConfirmLogout && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center">
          <div className="absolute inset-0 bg-black bg-opacity-40" onClick={() => setShowConfirmLogout(false)} />
          <div className="relative bg-white rounded-lg shadow-lg w-80 p-5 z-[61]">
            <h3 className="text-base font-semibold mb-2">Are you sure you want to logout?</h3>
            <p className="text-sm text-gray-600 mb-4">You will need to login again to continue.</p>
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 text-sm rounded border border-gray-300 text-gray-700 hover:bg-gray-50"
                onClick={() => setShowConfirmLogout(false)}
              >
                No
              </button>
              <button
                className="px-4 py-2 text-sm rounded text-white bg-red-500 hover:bg-red-600"
                onClick={handleLogout}
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Navbar;
